/*
  This component acts as a wrapper that abstracts away the task of 
  retrieving the Loan File Worksheet (LFWS), holding the form state of all updates, saving updates, and updating the UI accordingly (showing loading state, etc.)

  The wrapped components (the "children" prop) will be handed helpers to orchestrate the specifics of which UI components are used, what triggers the update, etc.
*/
import React, { useState, useEffect } from "react";
import { Loading } from "carbon-components-react";
import { LoadingState } from "./shared/types";
import { WS_NAME } from "./shared/WorksheetEditor/wsDefs";
import {
  fetchWorksheets,
  getWsByName,
  prepareUpdatesForAPI,
  updateLoanFileWorksheet,
} from "./shared/WorksheetEditor/wsHelpers";

interface Props {
  app: any;
  children: any;
}

const LFWSInput: React.FC<Props> = ({ app, children }: Props) => {
  const [formUpdates, setFormUpdates] = useState(null);
  const [loading, setLoading] = useState(null);
  const [ws, setWS] = useState(null);

  // Loads the LFWS and sets it to the current state
  const loadLoanFileWorksheet = () => {
    fetchWorksheets(app)
      .then(worksheets => {
        const defaultsWS = getWsByName(worksheets, WS_NAME.LOAN_FILE_DEFAULTS);

        if (!defaultsWS || !defaultsWS.instance) {
          throw new Error(`could not load prepaids worksheet on app ${app.id}`);
        }

        setWS({
          id: defaultsWS.id,
          version: defaultsWS.instance.version,
          instance: defaultsWS.instance,
        });
        setLoading(LoadingState.FINISHED);
      })
      .catch(err => {
        console.error(err);
        setLoading(LoadingState.FINISHED);
      });
  };

  // Initial load
  useEffect(() => {
    loadLoanFileWorksheet();
  }, []);

  // Using the formUpdates hook, will prepare an update payload, fire it off, then reload the LFWS
  const updateWorksheet = () => {
    if (!formUpdates) {
      // no updates -- nothing to do.
      return Promise.resolve(null);
    }

    setLoading(LoadingState.ACTIVE);

    const updates = prepareUpdatesForAPI(formUpdates);
    return updateLoanFileWorksheet(ws, updates)
      .then(() => loadLoanFileWorksheet())
      .then(() => {
        setFormUpdates(null); // Restore formUpdate state to null to reset the form
        setLoading(LoadingState.FINISHED);
      });
  };
  const handleChange = (key: string, value: any) => {
    setFormUpdates({
      ...formUpdates,
      [key]: value,
    });
  };

  const wsStateAndHelpers = {
    ws,
    formUpdates,
    handleChange,
    updateWorksheet,
  };

  return (
    <>
      {loading === LoadingState.ACTIVE && <Loading active />}
      {loading === LoadingState.FINISHED && ws && (
        <>{typeof children === "function" ? children(wsStateAndHelpers) : children}</>
      )}
    </>
  );
};

export default LFWSInput;
